import bodybuilder from 'bodybuilder';
import { ElasticApiResponse, RootApiResourceListResponseInterface, Hit } from 'polly-commons';
import { ES_WORKSPACES_INDICES } from '../../../modules/workspace-module/polly-manage-workspace.constants';
import { API_QUERY_PARAMS, FileType } from '../../../polly-manage.constants';
import { SelectedContentSortParams, WorkspaceContentItem, WorkspaceFileFilterParameters } from '../../interfaces';
import { getFormattedSearchString } from './workspaces.utilities';

/**
 * @description Elastic Search query builder for workspace content filters
 *
 * @param builder Bodybuilder instance
 * @param filters Currently selected workspace content filters
 * @param [filterByExtension] Optional parameter for filtering based on extension of files
 * @return bodybuilder.Bodybuilder
 */
function filtersQueryBuilder(
  builder: bodybuilder.Bodybuilder,
  filters: WorkspaceFileFilterParameters,
  filterByExtension?: string[]
): bodybuilder.Bodybuilder {
  const filterQueries: string[] = [];
  if (filters.filtersCleared) {
    filterQueries.push(...Object.values(FileType));
  } else {
    if (filters.showAnalysis) filterQueries.push(FileType.RUNS);
    if (filters.showFiles) filterQueries.push(FileType.FILE);
    if (filters.showNotebooks) filterQueries.push(FileType.NOTEBOOK);
    if (filters.showFolders) filterQueries.push(FileType.FOLDER);
  }
  builder.query('bool', (qBuilder: bodybuilder.Bodybuilder) => {
    if (filterByExtension) qBuilder.orQuery('terms', 'extension', filterByExtension);
    return qBuilder.orQuery('terms', 'entity_type', filterQueries);
  });
  return builder;
}

/**
 * @description Elastic search query builder for contents from document index
 *
 * @param workspaceId Current selected workspace, for which contents need to be loaded
 * @return bodybuilder.Bodybuilder
 */
function documentQueryBuilder(workspaceId: number): bodybuilder.Bodybuilder {
  const builder = bodybuilder();
  builder
    .andQuery('term', '_index', { value: ES_WORKSPACES_INDICES.CONTENT_INDEX })
    .andQuery('term', 'workspace_id', { value: workspaceId.toString() });
  return builder;
}

const permissionQuery = (builder: bodybuilder.Bodybuilder): bodybuilder.Bodybuilder => {
  return builder
    .andQuery('term', '_index', { value: ES_WORKSPACES_INDICES.WORKSPACE_INDEX })
    .query('nested', { path: 'permissions' }, (q: bodybuilder.Bodybuilder) =>
      q.query('match', 'permissions.user_id', { query: '{user_id}' })
    );
};

/**
 * @description Builds the elastic search query for listing the content of workspace/folder
 *
 * @param fromPage Starting number to fetch data from paginated ES APIs
 * @param workspaceId ID of the workspace
 * @param parentId ID of folder or workspaceID (for root level listing)
 * @param searchString query used to search workspace contents
 * @param sortParams parameters used for sorting content
 * @param filters Filters applied on workspace content based on type
 * @param filterByExtension filter presenting whether to filter by extension (used in OA to fetch only pdf and html files)
 *
 * @return object - Final elastic search query to be sent in payload
 */
export function buildContentListingQuery(
   fromPage: number,
   workspaceId: number,
   parentId: string,
   searchString?: string,
   sortParams?: SelectedContentSortParams,
   filters?: WorkspaceFileFilterParameters,
   filterByExtension?: string[]
 ): object {
   const documentQuery = (builder: bodybuilder.Bodybuilder): bodybuilder.Bodybuilder => {
     builder = documentQueryBuilder(workspaceId).andQuery('term', 'parent_id', { value: parentId });
     if (!!searchString) {
       builder.andQuery('query_string', { default_field: 'entity_name', query: `*${getFormattedSearchString(searchString)}*` });
     }
     if (!!filters) {
       builder = filtersQueryBuilder(builder, filters, filterByExtension);
     }
     return builder;
   };
   const queryBuilder = bodybuilder()
     .from(fromPage)
     .size(API_QUERY_PARAMS.WORKSPACE_PAGE_SIZE_VALUE)
     .orQuery('bool', documentQuery)
     .orQuery('bool', permissionQuery);

   if (!!sortParams && !!sortParams.sortColumnName) {
     queryBuilder.sort([{ [sortParams.sortColumnName]: { order: sortParams.sortOrder } }]);
   }
   return queryBuilder.build();
 }

/**
 * @description Builds the elastic search query for listing the no. of contents present on workspace
 *
 * @param workspaceId Current selected workspace, for which contents need to be loaded
 * @param filters Currently selected workspace content filters
 * @return object - Final elastic search query to be sent in payload
 */
export function buildContentMetaDataListingQuery(workspaceId: number, filters: WorkspaceFileFilterParameters): object {
  const documentQuery = (builder: bodybuilder.Bodybuilder): bodybuilder.Bodybuilder => {
    builder = filtersQueryBuilder(documentQueryBuilder(workspaceId), filters);
    return builder;
  };
  const queryBuilder = bodybuilder().orQuery('bool', documentQuery).orQuery('bool', permissionQuery);

  return queryBuilder.build();
}

export function buildPinnedItemListQuery(workspaceId: number): object {
  const documentQuery = (builder: bodybuilder.Bodybuilder): bodybuilder.Bodybuilder => {
    builder
      .andQuery('term', '_index', { value: ES_WORKSPACES_INDICES.CONTENT_INDEX })
      .andQuery('term', 'workspace_id', { value: workspaceId.toString() });
    builder.addQuery('term', 'is_pinned', { value: true });
    return builder;
  };

  return bodybuilder()
    .size(API_QUERY_PARAMS.WORKSPACE_PAGE_SIZE_VALUE)
    .orQuery('bool', documentQuery)
    .orQuery('bool', permissionQuery)
    .build();
}

/**
 * @description Mid-layer function to convert ElasticSearch APIs Response to WorkspaceContentArray used in UI part
 * @deprecated Not being used as we are directly using ES Resrouce on UI components instead of doing this extra conversion
 * @param elasticResponse Response from workspace/_search API for content listing on workspace/folder level
 * @return RootApiResourceListResponseInterface<any>[]
 */
export function getWorkspaceContent(
  elasticResponse: ElasticApiResponse<WorkspaceContentItem, undefined>
): RootApiResourceListResponseInterface<any>[] {
  const contentArray: RootApiResourceListResponseInterface<any>[] = [];
  elasticResponse.hits.hits.forEach((contentItem: Hit<WorkspaceContentItem>) => {
    if (contentItem._source.entity_type === FileType.FILE || contentItem._source.entity_type === FileType.NOTEBOOK) {
      contentArray.push({
        data: [
          {
            id: contentItem._source.s3_key,
            type: contentItem._source.entity_type,
            attributes: {
              s3_key: contentItem._source.s3_key,
              last_modified: contentItem._source.modified_date * 1000,
              size: contentItem._source.size,
              file_name: contentItem._source.entity_name,
              is_pinned: contentItem._source.is_pinned,
              report_id: contentItem._source.report_id,
            },
            links: contentItem._source.links,
          },
        ],
      });
    } else if (contentItem._source.entity_type === FileType.RUNS) {
      contentArray.push({
        data: [
          {
            id: contentItem._id,
            type: contentItem._source.entity_type,
            attributes: {
              s3_key: contentItem._source.s3_key,
              modified_time: contentItem._source.modified_date * 1000,
              size: contentItem._source.size,
              name: contentItem._source.entity_name,
              is_pinned: contentItem._source.is_pinned,
              analysis_run_id: contentItem._source.analysis_run_id,
              created_time: contentItem._source.created_date && contentItem._source.created_date * 1000,
            },
            links: contentItem._source.links,
          },
        ],
      });
    } else if (contentItem._source.entity_type === FileType.FOLDER) {
      contentArray.push({
        data: [
          {
            id: contentItem._id,
            type: contentItem._source.entity_type,
            attributes: {
              s3_key: contentItem._source.s3_key,
              modified_time: contentItem._source.modified_date * 1000 === 0 ? '-' : contentItem._source.modified_date,
              size: contentItem._source.size === 0 ? '-' : contentItem._source.size,
              file_name: contentItem._source.entity_name,
            },
            links: contentItem._source.links,
          },
        ],
      });
    }
  });
  return contentArray;
}

export function buildReportIdQuery(workspaceId: number, s3key: string): object {
  const documentQuery = (builder: bodybuilder.Bodybuilder): bodybuilder.Bodybuilder => {
    builder
      .andQuery('term', '_index', { value: ES_WORKSPACES_INDICES.CONTENT_INDEX })
      .andQuery('term', 'workspace_id', { value: workspaceId.toString() })
      .andQuery('term', 's3_key', { value: s3key });
    return builder;
  };

  return bodybuilder()
    .size(API_QUERY_PARAMS.WORKSPACE_PAGE_SIZE_VALUE)
    .orQuery('bool', documentQuery)
    .orQuery('bool', permissionQuery)
    .build();
}
